import { Controller } from "@hotwired/stimulus";
import SlimSelect from "slim-select";

export default class extends Controller {
  static targets = [
    "dialog",
    "form",
    "parentSection",
    "section",
    "changeAssessmentKindButton",
    "validateButton",
    "select",
    "selectContainer",
  ];

  static classes = ["hidden"];

  static values = {
    submitTypeKey: String,
    validateSubmitType: String,
  };

  initialize() {
    this.previousValues = {};
  }

  /**
   * Sets up the SlimSelect instance.
   * The `selectContainer` target is used to position the dropdown since
   * SlimSelect does not know how to render in a fixed position container.
   */
  selectTargetConnected(target) {
    this.select = new SlimSelect({
      select: target,
      settings: {
        contentLocation: this.selectContainerTarget,
        contentPosition: "fixed",
        showSearch: false,
      },
      events: {
        beforeOpen: () => {
          this.selectContainerTarget.classList.remove(this.hiddenClass);
        },
        afterClose: () => {
          this.selectContainerTarget.classList.add(this.hiddenClass);
        },
      },
    });
  }

  selectTargetDisconnected() {
    this.select.destroy();
  }

  // Hides an open sections, including the parentSection, and shows the section from params.
  showSection({ params }) {
    const { sectionId } = params;
    this.parentSectionTarget.classList.add(this.hiddenClass);
    this.sectionTargets.forEach((section) => {
      if (section.id === sectionId) {
        this.storePreviousValues(section);
        section.classList.remove(this.hiddenClass);
      } else {
        section.classList.add(this.hiddenClass);
      }
    });
  }

  /**
   * Hides all the section targets, discarding their data, bringing back the
   * data stored in `this.previousValues` for any section that was open.
   * Shows the parent section when done.
   */
  discardSections() {
    this.sectionTargets.forEach((section) => {
      if (!section.classList.contains(this.hiddenClass)) {
        this.restorePreviousValues(section);
        section.classList.add(this.hiddenClass);
      }
    });
    this.parentSectionTarget.classList.remove(this.hiddenClass);
  }

  /**
   * Checks if an element is a restorable element:
   *  - is inside a section target
   *  - has a name
   *  - has an id
   *  - is not a button
   *  - is not a fieldset
   *  - is not a submit button
   */
  // eslint-disable-next-line class-methods-use-this
  isRestorableInput(section, element) {
    return (
      section.contains(element) &&
      !!element.name &&
      !!element.id &&
      element.type !== "button" &&
      element.type !== "fieldset" &&
      element.type !== "submit"
    );
  }

  /**
   * For a given section, finds all of the form's input elements in the
   * section, excluding buttons and none-input elements like fieldsets, storing
   * the current input value, or checked state in the case of checkbox and radio
   * inputs, in `this.previousValues` that can later be used to restore them.
   */
  storePreviousValues(section) {
    [...this.formTarget.elements].forEach((element) => {
      if (this.isRestorableInput(section, element)) {
        let value;
        if (element.type === "checkbox" || element.type === "radio") {
          value = element.checked;
        } else {
          value = element.value;
        }
        // eslint-disable-next-line no-param-reassign
        this.previousValues[element.id] = value;
      }
    });
  }

  /**
   * For a given section, finds all of the form's input elements in the
   * section, excluding buttons and none-input elements like fieldsets, and
   * restores the value/checked state from `this.previousValues`.
   *
   * Dispatches a `previous-value-restored` event to notify elements that may
   * need to do more work when their value changes programmatically.
   * Inputs can subscribe to this event to make more adjustments.
   */
  restorePreviousValues(section) {
    [...this.formTarget.elements].forEach((element) => {
      if (this.isRestorableInput(section, element)) {
        const value = this.previousValues[element.id];
        if (value !== undefined) {
          if (element.type === "checkbox" || element.type === "radio") {
            // eslint-disable-next-line no-param-reassign
            element.checked = value;
          } else {
            // eslint-disable-next-line no-param-reassign
            element.value = value;
          }
          // Dispatches a `previous-value-restored` to notify elements that may
          // need to do more work when their value changes programmatically.
          this.dispatch("previous-value-restored", { target: element });
          // Clears the used previous value.
          this.previousValues[element.id] = undefined;
        }
      }
    });
  }

  /**
   * Submits a `CHANGE_ASSESSMENT_KIND` submit type.
   */
  changeAssessmentKind() {
    if (this.hasChangeAssessmentKindButtonTarget) {
      this.changeAssessmentKindButtonTarget.click();
    }
  }

  /**
   * Submits a `VALIDATE` submit type.
   */
  validateForm() {
    if (this.hasValidateButtonTarget) {
      this.validateButtonTarget.click();
    }
  }

  /**
   * Submits a validate request when the bryq assessment dialog is discarded.
   * This forces the values that were brought back to be validated, and updates
   * the form accordingly.
   */
  dialogDiscarded({ target }) {
    if (target === this.dialogTarget) {
      this.validateForm();
    }
  }

  /**
   * Closes the main dialog when the form is submitted successfully, from the
   * main dialog and not from one of the sections.
   */
  submitEnd(event) {
    const { detail: { formSubmission = {}, success } = {} } = event;
    const { submitter = {} } = formSubmission;
    const { name: submitterName, value: submitterValue } = submitter;

    const isValidateSubmitType =
      submitterName === this.submitTypeKeyValue &&
      submitterValue === this.validateSubmitTypeValue;

    const isFromParentSection =
      this.parentSectionTarget.contains(submitter) &&
      !this.sectionTargets.some((section) => section.contains(submitter));

    if (isValidateSubmitType && isFromParentSection && success) {
      this.dialogTarget.close();
    }
  }
}
